import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { getJobStatusConfig, getRelevantOccurrence, useWorkDate, useWorkOrder, useWorkOrderSimilar } from '../../Data/Jobs';
import { usePreference } from '../../Data/Preference';
import { getDateStr } from '../../Components/Formatting/DateTime';
import { hashCode, useRedirect, useRouteLink, useSearchParamsOnce } from '../../Services/helpers';
import { hasOpenRecordedTimePeriod } from '../../Services/recordedTime';
import { IDs } from '../../Data/Misc';
import useDataSource, { resolveSource } from '../../Data/Local/dataSource';
import customerFeed from '../../Data/Local/Customer/feed';
import { useCustomerSync, useJobDoneQueue } from '../../Data/Local/Customer/hooks';
import { useLiveWorkDay } from '../../Data/Local/Job/hooks';
import { useSession } from '../../Components/Session';
import { Roles } from '../../Data/Users';
import MarkAsDone from '../Customer/MarkAsDone';
import Skip from '../Customer/Skip';
import ScheduleJob from '../Customer/ScheduleJob';
import Optimise from './Modals/Optimise';
import RecordedTime from './RecordedTime/Modal';

const WorkViewContext = createContext();

export const useWorkViewContext = () => {
    return useContext(WorkViewContext);
};

export const Provider = ({ children, date, filterOverride }) => {
    const [persistedAssignedTo, setPersistedAssignedTo] = usePreference('work:assignedTo', null);
    const [persistedRoundId, setPersistedRoundId] = usePreference('work:round', null, true);
    const [persistedHideCompleted, setPersistedHideCompleted] = usePreference('work:hideCompleted', false);
    const [showRecordedTime, setShowRecordedTime] = usePreference('work:showRecordedTime', false);
    const [markingJobDone, setMarkingJobDone] = useState(null);
    const [skipping, setSkipping] = useState(null);
    const [scheduling, setScheduling] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const [isOrderInvalid, setIsOrderInvalid] = useState(false);
    const [optimiseOpen, setOptimiseOpen] = useState(false);
    const [editingRecordedTime, setEditingRecordedTime] = useState(null);
    const {go, Redirect} = useRedirect();
    const {user, isRoleSufficient} = useSession();
    const routeLink = useRouteLink();

    // Jobs ride the `customer` feed, so that is the feed the day's read asks about
    // — and the same answer decides whether the one write is durable.
    const source = useDataSource(customerFeed.name);
    const outboxActive = source.isLocal;
    const {jobOutboxStatus, jobDonePayload, markJobDone, cancelJobDone, retryJobDone}
        = useJobDoneQueue(outboxActive);

    const syncStatus = useCustomerSync(outboxActive);
    // `JobsQueryOptions::fromRequest` forces a non-manager's `assignedTo` to their
    // own id, so the API only ever sends them their own jobs. A worker's device
    // holds the whole subtree of every customer they have a job on, so the local
    // read has to apply the same narrowing or it would show a colleague's work.
    const local = useLiveWorkDay(date, outboxActive, {
        assignedTo: isRoleSufficient(Roles.Manager) ? null : (user?.id ?? null),
    });
    const api = useWorkDate(date, null, true, {block: outboxActive});
    const {data: jobs} = resolveSource(source, {
        local: local.jobs,
        api: api.jobs,
        isLoading: api.isLoading,
    });

    // Locally there is no request to have failed: the day renders from the device,
    // and a mutation reconciles through the next delta, so that is what a refresh
    // means here.
    const offlineUnavailable = outboxActive ? false : api.offlineUnavailable;
    const refreshJobs = outboxActive ? () => syncStatus.sync() : api.refresh;

    // Non-managers can't open the filter menu and only ever receive their own
    // jobs from the server. Ignore any persisted/URL-seeded user or round filter
    // for them, otherwise a stale value (e.g. after a demotion, or a shared
    // ?assignedTo=/?roundId= link) would hide every job with no way to clear it.
    const canFilter = isRoleSufficient(Roles.Manager);
    const searchData = useSearchParamsOnce();

    useEffect(() => {
        if (searchData?.assignedTo) {
            setPersistedAssignedTo(searchData.assignedTo);
        }
        if (searchData?.roundId) {
            setPersistedRoundId(searchData.roundId);
        }
        if (searchData?.hideCompleted) {
            setPersistedHideCompleted(searchData.hideCompleted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchData]);

    const assignedTo = useMemo(() => {
        if (filterOverride?.assignedTo !== undefined) {
            return filterOverride.assignedTo;
        }
        return canFilter ? persistedAssignedTo : null;
    }, [filterOverride?.assignedTo, persistedAssignedTo, canFilter]);

    const roundId = useMemo(() => {
        if (filterOverride?.roundId !== undefined) {
            return filterOverride.roundId;
        }
        return canFilter ? persistedRoundId : null;
    }, [filterOverride?.roundId, persistedRoundId, canFilter]);

    const hideCompleted = useMemo(() => {
        if (filterOverride?.hideCompleted !== undefined) {
            return filterOverride.hideCompleted;
        }
        // Same reasoning as assignedTo/roundId above: non-managers can't open the
        // filter menu, so a stale/seeded "hide completed" would permanently hide
        // their completed jobs with no way to turn it back on.
        return canFilter ? persistedHideCompleted : false;
    }, [filterOverride?.hideCompleted, persistedHideCompleted, canFilter]);

    // An order is stored per (date, assignedTo, roundId), with null meaning the
    // calendar's unassigned column - so the sentinels the job filters use have to
    // resolve to a real scope before they reach the API, or the request matches no
    // order (IDs.ONE) / the wrong one.
    const workOrderScope = useMemo(() => {
        return {
            date,
            assignedTo: (!canFilter || assignedTo === IDs.ONE)
                ? (user?.id ?? null)
                : (assignedTo === IDs.EMPTY ? null : assignedTo),
            roundId: roundId === IDs.EMPTY ? null : roundId,
        };
    }, [date, canFilter, assignedTo, roundId, user?.id]);

    const {order, reorder, optimise, refresh: refreshOrder, isLoading: workOrderLoading, deleteWorkOrder} = useWorkOrder(workOrderScope, true);

    const dateStr = useMemo(() => getDateStr(date, false, true), [date]);

    const expandNext = () => {
        const open = expanded ?? markingJobDone?.id ?? skipping?.id ?? scheduling?.id ?? null;

        if (!filteredJobs || !open) {
            return;
        }

        const next = findNextJob(filteredJobs, open);
        setExpanded(next ?? null);
    }

    const refreshAndExpandNext = () => {
        expandNext();
        refreshJobs();
    }

    const goBack = () => {
        const d = new Date(date+'T12:12:12Z');
        d.setDate(d.getDate() - 1);
        go('/work/'+d.toISOString().split('T')[0]);
    }

    const goForward = () => {
        const d = new Date(date+'T12:12:12Z');
        d.setDate(d.getDate() + 1);
        go('/work/'+d.toISOString().split('T')[0]);
    }

    useEffect(() => {
        refreshOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedTo, roundId, date]);

    // No queue-drained re-read: this day reads the local store, so a queued write
    // shows the moment it is queued and the flush reconciles through the sync
    // delta on its own.

    const roundIds = useMemo(() => {
        return jobs?.map(j => j.roundId).filter(id => id) ?? [];
    }, [jobs]);

    const filteredJobs = useMemo(() => {
        if (!jobs) {
            return [];
        }

        let tmpJobs = jobs;

        if (order && order.jobIds && order.jobIds.length > 0) {
            const orderedJobs = order.jobIds.map(id => jobs.find(j => j.id === id)).filter(j => j);
            const unorderedJobs = jobs.filter(j => !order.jobIds.includes(j.id));
            tmpJobs = [...orderedJobs, ...unorderedJobs];
        }

        const filtered = tmpJobs.filter(j => {
            if (assignedTo === null) {
                return true;
            }

            if (assignedTo === IDs.EMPTY) {
                return j.assignedTo === null;
            }

            if (assignedTo === IDs.ONE) {
                return j.assignedTo === null || j.assignedTo === user.id;
            }

            return j.assignedTo === assignedTo;
        })
        .filter(j => {
            if (roundId === null) {
                return true;
            }

            if (roundId === IDs.EMPTY) {
                return j.roundId === null;
            }

            return j.roundId === roundId;
        });

        return filtered;

    }, [jobs, assignedTo, roundId, order, user?.id]);

    const filteredWithoutCompleted = useMemo(() => {
        return filteredJobs.filter(j => {
            if (!hideCompleted) {
                return true;
            }
            // Match the occurrence-aware "done" state used to render each job
            // (see Work/Job.jsx) so recurring jobs completed for the viewed date
            // are hidden too, not just those whose aggregate status is "done".
            const jobStatus = getJobStatusConfig(j, getRelevantOccurrence(j, date));
            return !(jobStatus.considerDone ?? false) || j.id === expanded;
        })
    }, [filteredJobs, hideCompleted, expanded, date]);

    useEffect(() => {
        if (!order || !filteredJobs || !order.jobIds || !filteredJobs.length) {
            setIsOrderInvalid(false);
            return;
        }

        const jobOrderIds = [...order.jobIds].sort();
        const jobIds = [...filteredJobs.map(j => j.id)].sort();

        if (JSON.stringify(jobOrderIds) !== JSON.stringify(jobIds)) {
            setIsOrderInvalid(hashCode(JSON.stringify(jobIds)));
        } else {
            setIsOrderInvalid(false);
        }
    }, [order, filteredJobs]);

    const dateOptions = useMemo(() => {
        return [-2, -1, 0, 1, 2, 3, 4].map(i => {
            const d = new Date(date+'T12:12:12Z');
            d.setDate(d.getDate() + i);
            return {
                icon: i < 0 ? 'UpChevron' : i > 0 ? 'DownChevron' : 'Circle',
                title: getDateStr(d.toISOString().split('T')[0], false, true),
                onClick: () => go('/work/'+d.toISOString().split('T')[0]),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // Getting a similar work order must happen after the filtered jobs are set

    const {workOrder: similarWorkOrder, refresh: refreshSimilarWorkOrder} = useWorkOrderSimilar(
        filteredJobs?.map(j => j.id) ?? [],
        !!order?.id || workOrderLoading
    );

    const applySimilarWorkOrder = () => {
        const matches = similarWorkOrder.jobIds.filter(id => filteredJobs.some(j => j.id === id));
        const missing = filteredJobs.filter(j => !similarWorkOrder.jobIds.includes(j.id)).map(j => j.id);
        const all = [...matches, ...missing];

        return reorder(all);
    }

    const handleOptimise = async (optimiseOptions) => {
        const jobIds = filteredJobs.map(j => j.id);

        return optimise(jobIds, optimiseOptions);
    }

    useEffect(() => {
        refreshSimilarWorkOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredJobs, order?.id]);

    const editingRecordedTimeJob = editingRecordedTime?.job
        && !hasOpenRecordedTimePeriod(editingRecordedTime?.occurrence?.recordedTime)
        ? editingRecordedTime.job
        : null;

    const providerValues = {
        jobs,
        filteredJobs,
        filteredWithoutCompleted,
        date,
        dateStr,
        dateOptions,
        markingJobDone,
        setMarkingJobDone,
        skipping,
        setSkipping,
        scheduling,
        setScheduling,
        expanded,
        setExpanded,
        calendarOpen,
        setCalendarOpen,
        isReordering,
        setIsReordering,
        preferencesOpen,
        setPreferencesOpen,
        order,
        optimise: () => setOptimiseOpen(true),
        handleOptimise,
        saveOrder: reorder,
        workOrderLoading,
        deleteWorkOrder,
        refreshJobs,
        refreshOrder,
        refreshAndExpandNext,
        goBack,
        goForward,
        assignedTo,
        setAssignedTo: setPersistedAssignedTo,
        roundId,
        setRoundId: setPersistedRoundId,
        roundIds,
        hideCompleted,
        setHideCompleted: setPersistedHideCompleted,
        showRecordedTime,
        setShowRecordedTime,
        editingRecordedTime,
        setEditingRecordedTime,
        similarWorkOrder: (similarWorkOrder && similarWorkOrder?.length !== 0) ? similarWorkOrder : null,
        refreshSimilarWorkOrder,
        applySimilarWorkOrder,
        isOrderInvalid,
        offlineUnavailable,
        openRouteLink: () => routeLink(filteredJobs),

        outboxActive,
        jobOutboxStatus,
        cancelJobDone,
        retryJobDone,
    };

    return (
        <WorkViewContext.Provider value={providerValues}>
            {children}

            <MarkAsDone job={markingJobDone}
                onClose={() => setMarkingJobDone(null)}
                then={refreshAndExpandNext}
                actionSource="Work"
                markJobDone={outboxActive && markingJobDone
                    ? (jobId, data) => markJobDone(markingJobDone.customerId, jobId, data)
                    : undefined}
                queued={jobDonePayload[markingJobDone?.id]}
            />
            <Skip job={skipping}
                onClose={() => setSkipping(null)}
                then={refreshAndExpandNext}
            />
            <ScheduleJob job={scheduling}
                onClose={() => setScheduling(null)}
                then={refreshAndExpandNext}
            />
            <Optimise
                isOpen={optimiseOpen}
                setIsOpen={setOptimiseOpen}
            />
            <RecordedTime
                job={editingRecordedTimeJob}
                occurrence={editingRecordedTime?.occurrence}
                onClose={() => setEditingRecordedTime(null)}
                then={refreshJobs}
            />
            <Redirect />
        </WorkViewContext.Provider>
    );
};

const findNextJob = (filteredJobs, jobId) => {
    const index = filteredJobs.findIndex(j => j.id === jobId);

    if (index === -1) {
        return null;
    }

    const next = filteredJobs[index + 1] ?? null;

    return next ? next.id : null;
}
